import React from 'react';
import { X, ExternalLink, ShieldCheck } from 'lucide-react';
import { CertificationType } from '@/app/data';

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  certificateUrl?: string;
  certificationType?: CertificationType;
}

const getCertificationLabel = (type?: CertificationType): string => {
  switch (type) {
    case 'ECE':
      return 'ECE 22.06';
    case 'DOT':
      return 'DOT';
    case 'ECE_DOT':
      return 'ECE 22.06 y DOT';
    default:
      return 'Certificado';
  }
};

export const CertificateModal: React.FC<CertificateModalProps> = ({ 
  isOpen, 
  onClose, 
  productName,
  certificateUrl,
  certificationType
}) => {
  if (!isOpen) return null;

  const handleOpenCertificate = () => {
    if (certificateUrl) {
      window.open(certificateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const certLabel = getCertificationLabel(certificationType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col relative animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-white">
          <div>
            <h2 className="text-xl font-black italic text-[#061970] uppercase tracking-tighter">Certificación de Seguridad</h2>
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">{productName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-5">
            <ShieldCheck className="w-10 h-10 text-green-600" />
          </div>
          
          <h3 className="text-lg font-bold text-[#061970] mb-2">Producto Certificado</h3>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Este producto cumple con los estándares internacionales de seguridad <span className="font-bold text-[#061970]">{certLabel}</span>.
            Haz clic en el botón para ver el certificado oficial.
          </p>

          {certificateUrl ? (
            <button 
              onClick={handleOpenCertificate}
              className="w-full flex items-center justify-center gap-2 bg-[#061970] text-white px-6 py-4 rounded-full font-bold uppercase text-sm hover:bg-[#0a2399] transition-colors shadow-lg active:scale-95"
            >
              <ExternalLink className="w-4 h-4" />
              Ver Certificado Oficial
            </button>
          ) : (
            <p className="text-gray-400 text-sm">No hay certificado disponible</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button 
            onClick={onClose}
            className="w-full py-3 text-gray-500 font-bold uppercase text-xs hover:text-gray-700 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
